/*
 * Copyright 2025 Oddpods
 *
 * This Source Code Form is subject to the terms of the Mozilla Public
 * License, v. 2.0. If a copy of the MPL was not distributed with this
 * file, You can obtain one at https://mozilla.org/MPL/2.0/.
 *
 * See https://github.com/oddpods/cauce/blob/main/LICENSE
 */
package studio.oddpods.cauce.feature.settings

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.SharingStarted
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.stateIn
import kotlinx.coroutines.launch
import studio.oddpods.cauce.core.base.analytics.AnalyticsHelper
import studio.oddpods.cauce.core.data.repository.UserDataRepository
import studio.oddpods.cauce.core.data.repository.cauce.ProfileRepository
import studio.oddpods.cauce.core.model.DarkThemeConfig
import studio.oddpods.cauce.core.model.ThemeBrand
import studio.oddpods.cauce.core.model.display.DisplayPreferences
import studio.oddpods.cauce.core.network.auth.AuthService
import studio.oddpods.cauce.feature.settings.sections.ProfileSummaryState

class SettingsViewmodel(
    private val settingsRepository: UserDataRepository,
    private val profileRepository: ProfileRepository,
    private val authService: AuthService,
    private val analyticsHelper: AnalyticsHelper,
) : ViewModel() {

    private val profileState = MutableStateFlow(ProfileSummaryState())

    val settingsUiState: StateFlow<SettingsUiState> =
        combine(settingsRepository.userData, profileState) { userData, profile ->
            SettingsUiState.Success(
                data = SettingsUiData(
                    appearance = UserEditableSettings(
                        brand = userData.themeBrand,
                        useDynamicColor = userData.useDynamicColor,
                        darkThemeConfig = userData.darkThemeConfig,
                    ),
                    display = userData.displayPreferences,
                    profile = profile,
                ),
            )
        }
            .stateIn(
                scope = viewModelScope,
                started = SharingStarted.WhileSubscribed(5_000),
                initialValue = SettingsUiState.Loading,
            )

    init {
        loadProfileSummary()
    }

    private fun loadProfileSummary() {
        viewModelScope.launch {
            val profile = profileRepository.getCurrentProfile().getOrNull()
            profileState.value = ProfileSummaryState(
                displayName = profile?.displayName,
                email = authService.currentUserEmail,
            )
        }
    }

    fun updateThemeBrand(themeBrand: ThemeBrand) {
        viewModelScope.launch {
            analyticsHelper.logThemeBrandChanged(themeBrand)
            settingsRepository.setThemeBrand(themeBrand)
        }
    }

    fun updateDarkThemeConfig(darkThemeConfig: DarkThemeConfig) {
        viewModelScope.launch {
            analyticsHelper.logThemeChanged(darkThemeConfig)
            settingsRepository.setDarkThemeConfig(darkThemeConfig)
        }
    }

    fun updateDynamicColorPreference(useDynamicColor: Boolean) {
        viewModelScope.launch {
            analyticsHelper.logDynamicColorPreferences(useDynamicColor)
            settingsRepository.setDynamicColorPreference(useDynamicColor)
        }
    }

    fun onDisplayPrefsChanged(prefs: DisplayPreferences) {
        viewModelScope.launch {
            settingsRepository.setDisplayPreferences(prefs)
        }
    }

    fun onLogoutConfirmed() {
        viewModelScope.launch {
            analyticsHelper.logEvent(type = "settings_logout", params = emptyMap())
            runCatching { authService.signOut() }
            settingsRepository.clearUserData()
        }
    }
}

data class UserEditableSettings(
    val brand: ThemeBrand,
    val useDynamicColor: Boolean,
    val darkThemeConfig: DarkThemeConfig,
)

data class SettingsUiData(
    val appearance: UserEditableSettings,
    val display: DisplayPreferences,
    val profile: ProfileSummaryState,
)

sealed interface SettingsUiState {
    data object Loading : SettingsUiState
    data class Success(val data: SettingsUiData) : SettingsUiState
}

private fun AnalyticsHelper.logThemeBrandChanged(themeBrand: ThemeBrand) {
    logEvent(
        type = "theme_brand_changed",
        params = mapOf(
            "theme_brand" to themeBrand.name,
        ),
    )
}

private fun AnalyticsHelper.logDynamicColorPreferences(useDynamicColor: Boolean) {
    logEvent(
        type = "dynamic_color_preference_changed",
        params = mapOf(
            "use_dynamic_color" to useDynamicColor.toString(),
        ),
    )
}

private fun AnalyticsHelper.logThemeChanged(themeConfig: DarkThemeConfig) {
    logEvent(
        type = "dark_theme_config_changed",
        params = mapOf(
            "dark_theme_config" to themeConfig.name,
        ),
    )
}
